import { z } from 'zod';
import type { TestResult } from './project.js';

export type ExecStatusKey = TestResult | 'open';

export interface ExecutionStatus {
  total: number;
  byStatus: Record<ExecStatusKey, number>;
  executedPct: number; // (total - open) / total
  passedPct: number; // successful / total
}

export interface CoverageReport {
  inScopeProcesses: number;
  coveredProcesses: number; // in-scope processes with >=1 successful test case
  coveragePct: number;
  gaps: { process_id: string; name_de: string; name_en: string }[];
}

export interface SeverityBucket {
  severity_id: string | null;
  key: string;
  label_de: string;
  label_en: string;
  color_token: string;
  rank: number;
  openCount: number;
  blockerOnInScope: number; // open defects of this severity whose test case is on an in-scope process
}

export interface SeverityBreakdown {
  buckets: SeverityBucket[];
  totalOpen: number;
  blockerOnInScopeTotal: number; // rank 0 (Blocker) open defects on in-scope processes
}

export interface BurndownPoint {
  date: string; // YYYY-MM-DD
  planned: number; // linear plan curve, remaining in-scope test cases
  actual: number | null; // remaining not-yet-successful; null for future dates
}

export interface ForecastEta {
  projectedDate: string | null; // YYYY-MM-DD, null if velocity is 0
  runsPerDay: number; // successful/day over the trailing window
  remaining: number;
}

export interface BurndownReport {
  total: number;
  today: string; // YYYY-MM-DD
  points: BurndownPoint[];
  eta: ForecastEta;
}

export const EXIT_CRITERION_KINDS = [
  'coverage_pct',
  'execution_pct',
  'no_open_blocker',
  'no_open_high',
] as const;
export type ExitCriterionKind = (typeof EXIT_CRITERION_KINDS)[number];

export interface ExitCriterion {
  criterion_id: string;
  key: string;
  label_de: string;
  label_en: string;
  kind: ExitCriterionKind;
  threshold: number | null; // percent for *_pct kinds, null otherwise
  met: boolean;
  actual: number; // measured value (pct or open count)
  sort_order: number;
}

export interface ExitCriteriaReport {
  criteria: ExitCriterion[];
  // #86: 'not_configured' = es existiert kein einziges Exit-Kriterium. Bewusst ein DRITTER
  // Zustand statt 'go' — every() über eine leere Liste ist true, und eine Ampel, die ohne
  // Konfiguration grün zeigt, wäre eine stille Falschaussage.
  goNoGo: 'go' | 'no_go' | 'not_configured';
  evaluatedAt: string; // ISO
}

// #33: Abdeckungs-Auswertung der Simplifications. Projektweit (nicht plan-scoped): eine
// Simplification gehoert dem Katalog, nicht einem Plan. abgedeckt = >=1 verknuepfter Testfall
// (hat Vorrang), Ausnahme = keine Verknuepfung UND is_exception, not_covered = keine
// Verknuepfung UND nicht is_exception.
export interface SimplificationCoverage {
  covered: number;
  exception: number;
  not_covered: number;
  total: number;
}

// #69 (REP-6): "Tiefe" — läuft die komplette E2E-Kette durch? Bewusst eine EIGENE Kennzahl,
// getrennt von REP-3 (CoverageReport, "Breite": ein Prozess gilt schon ab einem bestandenen
// Testfall als abgedeckt — ODER-Semantik) und von der Ketten-Auswertung in runs.ts (dort nur für
// die Playlist-Sortierung, ebenfalls ODER: "mindestens ein Mitglied bestanden"). Hier gilt eine
// Kette nur als grün, wenn ALLE Mitglieder zuletzt bestanden haben (UND-Semantik).
export const E2eChainSchema = z.object({
  e2e_id: z.string().uuid(),
  human_id: z.string(),
  name_de: z.string(),
  // Review I4: NICHT nullable. `e2e_processes.name_en` ist in der DB `NOT NULL DEFAULT ''`
  // (apps/api/src/db/schema.ts) — eine unuebersetzte Kette traegt den LEEREN STRING, nie null.
  // Der urspruengliche `.nullable()` stammte aus dem Brief, widersprach dem Schema und hat in der
  // Anzeige den falschen Operator (`??` statt `||`) provoziert: `??` faengt '' nicht ab, die
  // englische Ansicht zeigte dann eine Kette ganz ohne Namen.
  name_en: z.string(),
  member_count: z.number().int(),
  passed_count: z.number().int(),
  // 1-basierte Position des ersten Mitglieds, das NICHT bestanden ist; null = Kette gruen.
  breaks_at: z.number().int().nullable(),
  breaks_at_title: z.string().nullable(),
  is_green: z.boolean(),
});
export type E2eChain = z.infer<typeof E2eChainSchema>;

export const E2eChainCoverageSchema = z.object({
  totalChains: z.number().int(),
  greenChains: z.number().int(),
  chainPct: z.number(),
  // Branch-Review B8: Zahl der NICHT auswertbaren Ketten (kein Mitglied im Plan-Scope bzw. nur
  // teilweise darin, oder ganz ohne Mitglieder). Ohne sie stand `chainEmpty` für zwei sehr
  // verschiedene Sachverhalte: "es gibt gar keine E2E-Ketten" und "jede Kette wurde verworfen,
  // weil sie nur halb im Plan liegt" — "0 %" sah dann aus wie "nichts vorhanden".
  skippedChains: z.number().int(),
  chains: z.array(E2eChainSchema),
});
export type E2eChainCoverage = z.infer<typeof E2eChainCoverageSchema>;

export interface ReportSummary {
  // #120: `node_ids` statt `node_id` — der Zuschnitt kann aus mehreren Knoten bestehen (eine
  // Rolle kann mehrfach zugewiesen sein). `null` = projektweit.
  scope: { test_plan_id: string; plan_name: string; node_ids: string[] | null };
  execution: ExecutionStatus;
  coverage: CoverageReport;
  severity: SeverityBreakdown;
  exitCriteria: ExitCriteriaReport;
  simplificationCoverage: SimplificationCoverage;
  e2eChainCoverage: E2eChainCoverage;
  // #92: Anzahl in-scope Testfälle, deren jüngster Lauf error/blocked ist UND die an einem
  // retest-bereiten Defect hängen (primär ODER #105-affected) — dieselbe Regel wie die
  // Retest-Sektion der Playlist (computeRetestDue, report-metrics.ts). Bei gesetztem node_id über
  // die knoten-gefilterten Testfälle gerechnet, analog `execution`.
  retestDueCount: number;
  // #97: Deltas "heute minus GESTERN-Snapshot" (plan_metric_snapshots, metric-snapshot.ts
  // loadYesterdaySnapshot). `null`, wenn kein Snapshot mit snapshot_date === Berlin-Gestern
  // existiert (Tag 1 nach Rollout oder eine Lücke im täglichen Tick) — dann wird nichts angezeigt,
  // statt eine falsche 0-Differenz vorzutäuschen.
  sinceYesterday: { passed: number; openBlocker: number } | null;
  // #97: Alter (volle Tage) des ältesten offenen Blocker-Defects (color_token 'blocker'), LIVE
  // gerechnet (nicht aus dem Snapshot). `null` ohne offene Blocker.
  oldestOpenBlockerDays: number | null;
}

export interface ForecastNarrative {
  text_de: string;
  confidence: 'hoch' | 'mittel' | 'gering';
  risk: 'im_plan' | 'gefaehrdet' | 'verzug';
}

export interface ForecastResponse {
  eta: ForecastEta;
  narrative: ForecastNarrative | null;
}

// #97: GET /reports/trend — Blocker/Hoch je ISO-Kalenderwoche aus den täglichen Snapshots
// (plan_metric_snapshots). Je Woche zählt der LETZTE Snapshot (jüngstes snapshot_date der Woche);
// aufsteigend sortiert. Reine Zeitreihe, KEIN Delta wie sinceYesterday — deshalb ein eigener
// Response-Typ statt eines Felds in ReportSummary.
export interface TrendWeek {
  week: string; // ISO-8601, 'YYYY-Www', z. B. '2026-W34'
  openBlocker: number;
  openHigh: number;
}

export interface TrendResponse {
  weeks: TrendWeek[];
}

// #98.2: GET /reports/streams — EINE Zeile je Struktur-Knoten (Teilprojekt/Stream) für die
// Stream-Tabelle im Reporting (design-spec.md §3.2: "Ampel · Fortschrittsbalken · offene Defects ·
// E2E-Ketten · Sign-off-Status"). Bewusst NICHT über /reports/summary?node_id geholt — die
// Deltas (sinceYesterday) haben keine Knoten-Dimension (Merkposten aus B-Task 5), und die
// Stream-Tabelle braucht ohnehin andere Felder (openDefects/openBlocker als absolute Zahlen statt
// severity-Buckets, e2eGreen/e2eTotal statt der vollen Ketten-Liste).
export interface StreamRow {
  node_id: string;
  name: string;
  kind: 'teilprojekt' | 'stream';
  passed: number; // in-scope Testfälle am Knoten mit jüngstem Lauf 'successful'
  total: number; // in-scope Testfälle am Knoten (Grundmenge)
  openDefects: number; // offene Defects (Status nicht geschlossen/abgelehnt) auf Knoten-Prozessen
  openBlocker: number; // Teilmenge von openDefects mit color_token 'blocker'
  e2eGreen: number;
  e2eTotal: number;
  // 'signed' = aktiver (nicht widerrufener) Sign-off; 'none' = keine In-Scope-Testfälle am Knoten
  // (nichts abzunehmen); 'pending' = es gibt etwas abzunehmen, aber (noch) keinen aktiven Sign-off.
  signoff: 'signed' | 'pending' | 'none';
}

export interface StreamsResponse {
  streams: StreamRow[];
}

// #98.3: GET /reports/testers — Fortschritt je Tester. Nur ZUGEWIESENE In-Scope-Plan-Items gehen
// ein (dieselbe Grundmenge wie scope-table.tsx' Zuweisungs-Fortschrittszeile); ein unzugewiesener
// Testfall erzeugt keine Zeile.
export interface TesterRow {
  user_id: string;
  display_name: string;
  assigned: number;
  // done = jüngster Lauf des Testfalls ist ENTSCHIEDEN (bestanden ODER fehlgeschlagen) — dieselbe
  // REP-1-Semantik wie plan-status.ts/metric-snapshot.ts: 'blocked'/'repeat'/'open' bleiben offen.
  done: number;
  open: number; // assigned - done
}

export interface TestersResponse {
  testers: TesterRow[];
}

export const ReportScopeQuerySchema = z.object({
  test_plan_id: z.string().uuid(),
  node_id: z.string().uuid().optional(),
});
export type ReportScopeQuery = z.infer<typeof ReportScopeQuerySchema>;
